import { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { useToast } from "../components/ui/ToastContainer";
import { campaignService } from "../services/campaignService";
import { parseApiError } from "../utils/errorHandler";
import { useNavigate, useParams } from "react-router-dom";
import { EmailEditor } from "../components/email-campaigns/EmailEditor";
import { TargetingForm } from "../components/email-campaigns/TargetingForm";
import { RecipientsPreview } from "../components/email-campaigns/RecipientsPreview";
import { ArrowLeft, ArrowRight, Loader2, Send } from "lucide-react";
import { courseService } from "../services/courseService";

export default function AddEditCampaignPage() {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [currentStep, setCurrentStep] = useState<
    "basics" | "content" | "targeting" | "schedule"
  >("basics");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "promotional",
    templateId: "",
    templateParams: {} as Record<string, any>,
    subject: "",
    preheader: "",
    targeting: {
      audienceType: "all",
      courses: [] as string[],
      roles: [] as string[],
      lastActivityDays: { min: "", max: "" },
      specificStudents: [] as string[],
    },
    schedule: {
      sendImmediately: true,
    },
  });

  const [courses, setCourses] = useState<{ _id: string; title: string }[]>([]);

  const [recipients, setRecipients] = useState<any[]>([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const response = await courseService.getAllCourses({ limit: 100 });
        console.log("Courses response:", response);
        setCourses((response.data?.courses || []) as unknown as typeof courses);
      } catch (error) {
        console.error("Failed to load courses:", error);
      }
    };
    loadCourses();

    if (id) {
      loadCampaign();
    }
  }, [id]);

  const loadCampaign = async () => {
    if (!id) return;
    try {
      const response = await campaignService.getCampaign(id);
      const campaign = response.data;
      setFormData({
        title: campaign.title || "",
        description: campaign.description || "",
        type: campaign.type || "promotional",
        templateId: campaign.templateId ? String(campaign.templateId) : "",
        templateParams: campaign.templateParams || {},
        subject: campaign.subject || "",
        preheader: campaign.preheader || "",
        targeting: {
          audienceType: campaign.targeting?.audienceType || "all",
          courses: campaign.targeting?.courses || [],
          roles: campaign.targeting?.roles || [],
          lastActivityDays: (campaign.targeting?.lastActivityDays as {
            min: string;
            max: string;
          }) || {
            min: "",
            max: "",
          },
          specificStudents: campaign.targeting?.specificStudents || [],
        },
        schedule: {
          sendImmediately: true,
        },
      });
    } catch (error) {
      console.error("Failed to load campaign:", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Failed to Load Campaign");
    }
  };

  const calculateRecipients = useCallback(async () => {
    // Skip API calculation when using specific students
    if (formData.targeting.audienceType === "custom") {
      const specificCount = (formData.targeting.specificStudents || []).length;
      console.log("Using specific students count:", specificCount);
      setRecipientCount(specificCount);
      setRecipients([]);
      return;
    }

    setIsLoadingRecipients(true);
    try {
      console.log("Calculating recipients with targeting:", formData.targeting);
      const response = await campaignService.calculateRecipients(
        formData.targeting,
      );
      console.log("Recipients response:", response);
      setRecipients(response.recipients || []);
      setRecipientCount(response.count || 0);
      console.log("Recipient count set to:", response.count || 0);
    } catch (error) {
      console.error("Failed to calculate recipients:", error);
      const apiError = parseApiError(error);
      console.log("API Error message:", apiError.message);
      showError(apiError.message, "Calculation Failed");
    } finally {
      setIsLoadingRecipients(false);
    }
  }, [formData.targeting, showError]);

  useEffect(() => {
    calculateRecipients();
  }, [formData.targeting, calculateRecipients]);

  // Debug: Track targeting changes
  useEffect(() => {
    console.log("Targeting changed:", formData.targeting);
  }, [formData.targeting]);

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const campaignData = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        templateId: Number(formData.templateId),
        templateParams: formData.templateParams,
        subject: formData.subject,
        preheader: formData.preheader,
        targeting: formData.targeting,
        schedule: {
          sendImmediately: false,
        },
      };

      let response;
      if (isEdit) {
        response = await campaignService.updateCampaign(id, campaignData);
      } else {
        response = await campaignService.createCampaign(campaignData);
      }

      showSuccess(
        isEdit
          ? "Campaign updated successfully"
          : "Campaign created successfully",
        "Success",
      );
      if (isEdit) {
        navigate(`/email-campaigns/edit/${response.data._id}`);
      } else {
        navigate(`/email-campaigns/edit/${response.data._id}`);
      }
    } catch (error) {
      console.error("Failed to save campaign:", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Save Failed");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSend = async () => {
    setIsSending(true);
    try {
      const campaignData = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        templateId: Number(formData.templateId),
        templateParams: formData.templateParams,
        subject: formData.subject,
        preheader: formData.preheader,
        targeting: formData.targeting,
        schedule: {
          sendImmediately: true,
        },
      };

      if (isEdit) {
        // Always save latest changes first
        await campaignService.updateCampaign(id, campaignData);
        await campaignService.sendCampaign(id);
      } else {
        const response = await campaignService.createCampaign(campaignData);
        await campaignService.sendCampaign(response.data._id);
      }

      showSuccess("Campaign sent successfully!", "Success");
      navigate("/email-campaigns");
    } catch (error) {
      console.error("Failed to send campaign:", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Send Failed");
    } finally {
      setIsSending(false);
    }
  };

  const steps = [
    { id: "basics", label: "Basics", icon: "1" },
    { id: "content", label: "Content", icon: "2" },
    { id: "targeting", label: "Targeting", icon: "3" },
    { id: "schedule", label: "Review & Send", icon: "4" },
  ];

  const canProceedToNext = () => {
    switch (currentStep) {
      case "basics":
        return formData.title.trim().length >= 3;
      case "content":
        return formData.templateId.length > 0;
      case "targeting":
        // For "custom", require at least one student selected
        if (formData.targeting.audienceType === "custom") {
          return (formData.targeting.specificStudents || []).length > 0;
        }
        // For other audience types, just require the selection to be valid
        return formData.targeting.audienceType !== "";
      case "schedule":
        return true;
      default:
        return true;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/email-campaigns")}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#989797] hover:text-[#102040] transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Campaigns
            </button>
            <h1 className="text-2xl font-bold text-[#102040]">
              {isEdit ? "Edit Campaign" : "Create Campaign"}
            </h1>
          </div>
          {isEdit && (
            <div className="text-sm text-[#989797]">
              Campaign ID:{" "}
              <span className="font-mono text-[#102040]">{id}</span>
            </div>
          )}
        </div>

        <Card className="p-6">
          <div className="flex items-center gap-4 mb-6">
            {steps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => {
                  if (
                    canProceedToNext() ||
                    steps.findIndex((s) => s.id === step.id) <
                      steps.findIndex((s) => s.id === currentStep)
                  ) {
                    setCurrentStep(step.id as any);
                  }
                }}
                disabled={
                  !canProceedToNext() &&
                  steps.findIndex((s) => s.id === step.id) >
                    steps.findIndex((s) => s.id === currentStep)
                }
                className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  currentStep === step.id
                    ? "bg-[#e07828] text-white"
                    : canProceedToNext()
                      ? "bg-white text-[#102040] hover:bg-gray-50 border border-gray-200"
                      : "bg-gray-100 text-[#989797] cursor-not-allowed"
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    currentStep === step.id
                      ? "bg-white text-[#e07828]"
                      : canProceedToNext()
                        ? "bg-[#e07828] text-white"
                        : "bg-gray-300 text-gray-500"
                  }`}
                >
                  {step.icon}
                </div>
                <span>{step.label}</span>
                {index < steps.length - 1 && (
                  <ArrowRight size={16} className="text-[#989797]" />
                )}
              </button>
            ))}
          </div>

          {currentStep === "basics" && (
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#102040]">
                  Campaign Title *
                </label>
                <Input
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="e.g., Summer Sale Announcement"
                />
                <p className="text-xs text-[#989797]">
                  3-100 characters - for your internal tracking
                </p>
              </div>
            </div>
          )}

          {currentStep === "content" && (
            <div className="space-y-6">
              <EmailEditor
                templateId={formData.templateId}
                onTemplateIdChange={(templateId) =>
                  setFormData({ ...formData, templateId })
                }
                templateParams={formData.templateParams}
                onTemplateParamsChange={(templateParams) =>
                  setFormData({ ...formData, templateParams })
                }
                subject={formData.subject}
                preheader={formData.preheader}
                onSubjectChange={(subject) =>
                  setFormData({ ...formData, subject })
                }
                onPreheaderChange={(preheader) =>
                  setFormData({ ...formData, preheader })
                }
              />
            </div>
          )}

          {currentStep === "targeting" && (
            <div className="space-y-6">
              <TargetingForm
                targeting={formData.targeting}
                onChange={(targeting) =>
                  setFormData({ ...formData, targeting })
                }
                courses={courses}
              />

              {recipientCount > 0 &&
                formData.targeting.audienceType !== "all" &&
                formData.targeting.audienceType !== "custom" && (
                  <RecipientsPreview
                    count={recipientCount}
                    recipients={recipients}
                    isLoading={isLoadingRecipients}
                    onCalculate={calculateRecipients}
                  />
                )}
            </div>
          )}

          {currentStep === "schedule" && (
            <div className="space-y-6">
              <div className="flex items-start gap-4 p-5 bg-[#FFF9E6] border border-[#f09040] rounded-xl">
                <div className="p-2 bg-white rounded-full border border-[#e07828] text-[#e07828] shrink-0">
                  <Send size={20} />
                </div>
                <div>
                  <h4 className="text-base font-bold text-[#102040] mb-1">
                    Ready to Send
                  </h4>
                  <p className="text-sm text-[#686868]">
                    This campaign will be sent <strong>immediately</strong> to{" "}
                    <span className="text-[#e07828] font-bold">
                      {recipientCount}
                    </span>{" "}
                    recipients. Please review your content and targeting before
                    proceeding.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-6 border-t border-gray-100 mt-6">
            {currentStep !== "basics" && (
              <Button
                variant="outline"
                onClick={() => {
                  const currentIndex = steps.findIndex(
                    (s) => s.id === currentStep,
                  );
                  if (currentIndex > 0) {
                    setCurrentStep(steps[currentIndex - 1]?.id as any);
                  }
                }}
              >
                Previous
              </Button>
            )}

            <div className="flex items-center gap-3">
              {currentStep !== "schedule" && (
                <Button
                  onClick={() => {
                    const currentIndex = steps.findIndex(
                      (s) => s.id === currentStep,
                    );
                    if (currentIndex < steps.length - 1) {
                      setCurrentStep(steps[currentIndex + 1]?.id as any);
                    }
                  }}
                  disabled={!canProceedToNext()}
                >
                  {currentStep === "basics" && "Next: Content"}
                  {currentStep === "content" && "Next: Targeting"}
                  {currentStep === "targeting" && "Next: Review & Send"}
                </Button>
              )}

              {currentStep === "schedule" && (
                <>
                  <Button
                    onClick={handleSaveDraft}
                    disabled={isSaving}
                    className="bg-gray-600 hover:bg-gray-700"
                  >
                    {isSaving ? (
                      <span className="flex items-center gap-2">
                        <Loader2 size={16} className="animate-spin" />
                        Saving Draft...
                      </span>
                    ) : (
                      "Save as Draft"
                    )}
                  </Button>

                  <Button
                    onClick={handleSend}
                    disabled={isSending || !canProceedToNext()}
                  >
                    {isSending ? (
                      <span className="flex items-center gap-2">
                        <Loader2 size={16} className="animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send size={16} />
                        Send Now
                      </span>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
